import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import {
    AcademicCapIcon,
    BookOpenIcon,
    UserGroupIcon,
    QuestionMarkCircleIcon,
    ChartBarIcon,
    ArrowTrendingUpIcon,
    DocumentTextIcon,
    PhotoIcon,
    CogIcon,
    CheckBadgeIcon,
    PencilSquareIcon,
    CalendarIcon,
    EyeIcon,
    HeartIcon
} from '@heroicons/react/24/outline'
import { adminAPI } from '../../services/api'
import LoadingSpinner from '../../components/Common/LoadingSpinner'
import ErrorMessage from '../../components/Common/ErrorMessage'
import { useAuth } from '../../hooks/useAuth'

const AdminDashboard = () => {
    const { user } = useAuth()
    const { data, isLoading, error } = useQuery({
        queryKey: ['admin-dashboard'],
        queryFn: adminAPI.getDashboard,
    })

    // Helper function to check if user has permission for a section
    const hasPermission = (section, action = 'read') => {
        if (!user || !user.permissions) return false
        if (user.role === 'admin') return true // Admin has all permissions
        return user.permissions[section] && user.permissions[section][action]
    }

    // Get user role display name
    const getUserRoleDisplay = () => {
        if (!user) return 'Admin'
        switch (user.role) {
            case 'admin': return 'Administrator'
            case 'editor': return 'Editor'
            case 'viewer': return 'Viewer'
            default: return 'User'
        }
    }

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <LoadingSpinner size="lg" />
            </div>
        )
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <ErrorMessage message="Failed to load dashboard data" />
            </div>
        )
    }

    const {
        overview,
        enquiry_breakdown,
        enquiry_by_source,
        university_by_state,
        course_by_category,
        popular_courses,
        popular_universities,
        recent_activity
    } = data.data

    // Get stat cards based on user permissions
    const getStatCards = () => {
        const cards = []

        // Always show dashboard welcome
        cards.push({
            title: 'Welcome',
            value: user?.name || 'Admin',
            subtitle: getUserRoleDisplay(),
            icon: UserGroupIcon,
            color: 'text-blue-600',
            bgColor: 'bg-blue-100',
        })

        // Show universities if user has permission
        if (hasPermission('universities')) {
            cards.push({
                title: 'Total Universities',
                value: overview.total_universities,
                icon: AcademicCapIcon,
                color: 'text-primary-600',
                bgColor: 'bg-primary-100',
            })
        }

        // Show courses if user has permission
        if (hasPermission('courses')) {
            cards.push({
                title: 'Total Courses',
                value: overview.total_courses,
                icon: BookOpenIcon,
                color: 'text-secondary-600',
                bgColor: 'bg-secondary-100',
            })
        }

        // Show enquiries if user has permission
        if (hasPermission('enquiries')) {
            cards.push({
                title: 'Total Enquiries',
                value: overview.total_enquiries,
                icon: UserGroupIcon,
                color: 'text-accent-600',
                bgColor: 'bg-accent-100',
            })
            cards.push({
                title: 'New Enquiries',
                value: overview.new_enquiries,
                icon: ArrowTrendingUpIcon,
                color: 'text-gold-600',
                bgColor: 'bg-gold-100',
            })
        }

        // Show blogs if user has permission
        if (hasPermission('blogs')) {
            cards.push({
                title: 'Total Blogs',
                value: overview.total_blogs,
                icon: PencilSquareIcon,
                color: 'text-purple-600',
                bgColor: 'bg-purple-100',
            })
        }

        // Show FAQs if user has permission
        if (hasPermission('faqs')) {
            cards.push({
                title: 'Total FAQs',
                value: overview.total_faqs,
                icon: QuestionMarkCircleIcon,
                color: 'text-green-600',
                bgColor: 'bg-green-100',
            })
        }

        // Show banners if user has permission
        if (hasPermission('banners')) {
            cards.push({
                title: 'Active Banners',
                value: overview.total_banners,
                icon: PhotoIcon,
                color: 'text-indigo-600',
                bgColor: 'bg-indigo-100',
            })
        }

        // Show additional admin stats
        if (user?.role === 'admin') {
            cards.push({
                title: 'Total Counsellors',
                value: overview.total_counsellors,
                icon: UserGroupIcon,
                color: 'text-pink-600',
                bgColor: 'bg-pink-100',
            })
            cards.push({
                title: 'Recent Enquiries',
                value: overview.recent_enquiries,
                icon: CalendarIcon,
                color: 'text-orange-600',
                bgColor: 'bg-orange-100',
            })
        }

        return cards
    }

    const statCards = getStatCards()

    return (
        <>
            <Helmet>
                <title>Admin Dashboard | Margdarshak</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <div className="min-h-screen bg-gray-50">
                {/* Welcome Section */}
                <div className="bg-gradient-to-br from-primary-600 via-secondary-600 to-accent-600 text-white relative overflow-hidden">
                    {/* Background Pattern */}
                    <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>

                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="text-center"
                        >
                            <div className="mb-6">
                                <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
                                    <UserGroupIcon className="w-10 h-10 text-white" />
                                </div>
                                <h1 className="text-4xl font-bold mb-3 text-white">
                                    Welcome back, {user?.name || 'Admin'}!
                                </h1>
                                <p className="text-white/90 text-xl mb-6 text-white">
                                    {user?.role === 'admin' && 'System Administrator - Full System Access'}
                                    {user?.role === 'editor' && hasPermission('universities') && hasPermission('courses') && 'Academic Manager - Manage Universities, Courses & Master Data'}
                                    {user?.role === 'editor' && hasPermission('blogs') && hasPermission('faqs') && 'Content Manager - Manage Blogs & FAQs'}
                                    {user?.role === 'editor' && hasPermission('enquiries') && 'Enquiry Manager - Manage All Enquiries'}
                                    {!user?.role && 'Admin Dashboard'}
                                </p>
                            </div>

                            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                                <div className="bg-white/15 backdrop-blur-sm rounded-xl px-6 py-3 border border-white/20">
                                    <span className="text-sm font-medium text-white/90">
                                        {new Date().toLocaleDateString('en-US', {
                                            weekday: 'long',
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        })}
                                    </span>
                                </div>
                                <div className="bg-white/15 backdrop-blur-sm rounded-xl px-6 py-3 border border-white/20">
                                    <span className="text-sm font-medium text-white/90">
                                        {getUserRoleDisplay()}
                                    </span>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {/* Stats Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        {statCards.map((stat, index) => {
                            const Icon = stat.icon
                            return (
                                <motion.div
                                    key={stat.title}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-gray-200"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            <div className={`w-14 h-14 ${stat.bgColor} rounded-xl flex items-center justify-center mr-4 shadow-sm`}>
                                                <Icon className={`w-7 h-7 ${stat.color}`} />
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                                                <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                                                {stat.subtitle && (
                                                    <p className="text-xs text-gray-500 mt-1">{stat.subtitle}</p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                                        </div>
                                    </div>
                                </motion.div>
                            )
                        })}
                    </div>

                    {/* Charts and Tables */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Enquiry Breakdown - Only for users with enquiry permission */}
                        {hasPermission('enquiries') && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.4 }}
                                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
                            >
                                <div className="flex items-center mb-6">
                                    <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center mr-3">
                                        <ChartBarIcon className="w-5 h-5 text-accent-600" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900">Enquiry Status</h3>
                                </div>
                                <div className="space-y-4">
                                    {enquiry_breakdown.map((item, index) => (
                                        <div key={item._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                            <div className="flex items-center">
                                                <div className={`w-3 h-3 rounded-full mr-3 ${item._id === 'new' ? 'bg-blue-500' :
                                                    item._id === 'qualified' ? 'bg-green-500' :
                                                        item._id === 'contacted' ? 'bg-yellow-500' :
                                                            'bg-gray-500'
                                                    }`}></div>
                                                <span className="text-sm font-medium text-gray-700 capitalize">{item._id}</span>
                                            </div>
                                            <span className="font-bold text-gray-900 text-lg">{item.count}</span>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        )}

                        {/* Enquiry Sources - Only for users with enquiry permission */}
                        {hasPermission('enquiries') && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.5 }}
                                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
                            >
                                <div className="flex items-center mb-6">
                                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                                        <UserGroupIcon className="w-5 h-5 text-primary-600" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900">Enquiry Sources</h3>
                                </div>
                                <div className="space-y-4">
                                    {enquiry_by_source.map((item) => (
                                        <div key={item._id} className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg hover:from-gray-100 hover:to-gray-200 transition-all">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm font-medium text-gray-700 capitalize">{item._id.replace('_', ' ')}</span>
                                                <span className="font-bold text-gray-900 text-lg">{item.count}</span>
                                            </div>
                                            <div className="flex items-center justify-between text-xs text-gray-500">
                                                <span>{item.new} new</span>
                                                <span>{item.converted} converted</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        )}

                        {/* Popular Courses for Enquiries - Only for users with course permission */}
                        {hasPermission('courses') && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.5 }}
                                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
                            >
                                <div className="flex items-center mb-6">
                                    <div className="w-10 h-10 bg-secondary-100 rounded-lg flex items-center justify-center mr-3">
                                        <BookOpenIcon className="w-5 h-5 text-secondary-600" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900">Most Popular Courses</h3>
                                </div>
                                <div className="space-y-3">
                                    {popular_courses && popular_courses.length > 0 ? (
                                        popular_courses.map((item, index) => (
                                            <div key={item._id} className="flex items-center justify-between p-3 bg-gradient-to-r from-secondary-50 to-blue-50 rounded-lg hover:from-secondary-100 hover:to-blue-100 transition-all">
                                                <div className="flex items-center">
                                                    <div className="w-8 h-8 bg-secondary-100 rounded-full flex items-center justify-center mr-3 text-sm font-bold text-secondary-600">
                                                        {index + 1}
                                                    </div>
                                                    <span className="text-sm font-medium text-gray-700 truncate">
                                                        {item.courseTitle}
                                                    </span>
                                                </div>
                                                <span className="font-bold text-secondary-600 text-lg">{item.count}</span>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center py-8 text-gray-500">
                                            <BookOpenIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                            <p className="text-sm">No course enquiries yet</p>
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        )}

                        {/* Popular Universities for Enquiries - Only for users with university permission */}
                        {hasPermission('universities') && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.6 }}
                                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
                            >
                                <div className="flex items-center mb-6">
                                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                                        <AcademicCapIcon className="w-5 h-5 text-primary-600" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900">Most Popular Universities</h3>
                                </div>
                                <div className="space-y-3">
                                    {popular_universities && popular_universities.length > 0 ? (
                                        popular_universities.map((item, index) => (
                                            <div key={item._id} className="flex items-center justify-between p-3 bg-gradient-to-r from-primary-50 to-green-50 rounded-lg hover:from-primary-100 hover:to-green-100 transition-all">
                                                <div className="flex items-center">
                                                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-3 text-sm font-bold text-primary-600">
                                                        {index + 1}
                                                    </div>
                                                    <span className="text-sm font-medium text-gray-700 truncate">
                                                        {item.universityName}
                                                    </span>
                                                </div>
                                                <span className="font-bold text-primary-600 text-lg">{item.count}</span>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center py-8 text-gray-500">
                                            <AcademicCapIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                            <p className="text-sm">No university enquiries yet</p>
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        )}

                        {/* Admin Only - System Overview */}
                        {user?.role === 'admin' && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.7 }}
                                className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-2xl p-6 shadow-lg border border-primary-100"
                            >
                                <div className="flex items-center mb-6">
                                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                                        <CogIcon className="w-5 h-5 text-primary-600" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900">System Overview</h3>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-white rounded-lg p-4 text-center">
                                        <div className="text-2xl font-bold text-primary-600">{overview.total_banners}</div>
                                        <div className="text-xs text-gray-600">Banners</div>
                                    </div>
                                    <div className="bg-white rounded-lg p-4 text-center">
                                        <div className="text-2xl font-bold text-secondary-600">{overview.total_faqs}</div>
                                        <div className="text-xs text-gray-600">FAQs</div>
                                    </div>
                                    <div className="bg-white rounded-lg p-4 text-center">
                                        <div className="text-2xl font-bold text-accent-600">{overview.total_counsellors}</div>
                                        <div className="text-xs text-gray-600">Counsellors</div>
                                    </div>
                                    <div className="bg-white rounded-lg p-4 text-center">
                                        <div className="text-2xl font-bold text-gold-600">{overview.recent_enquiries}</div>
                                        <div className="text-xs text-gray-600">Recent (7d)</div>
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {/* Admin Only - Recent Activity Chart */}
                        {user?.role === 'admin' && recent_activity && recent_activity.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.8 }}
                                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
                            >
                                <div className="flex items-center mb-6">
                                    <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center mr-3">
                                        <ChartBarIcon className="w-5 h-5 text-accent-600" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900">Recent Activity (7 Days)</h3>
                                </div>
                                <div className="space-y-3">
                                    {recent_activity.map((item) => (
                                        <div key={item._id} className="flex items-center justify-between p-3 bg-gradient-to-r from-accent-50 to-gold-50 rounded-lg">
                                            <span className="text-sm font-medium text-gray-700">
                                                {new Date(item._id).toLocaleDateString('en-US', {
                                                    weekday: 'short',
                                                    month: 'short',
                                                    day: 'numeric'
                                                })}
                                            </span>
                                            <div className="flex items-center">
                                                <div className="w-16 bg-accent-200 rounded-full h-2 mr-3">
                                                    <div
                                                        className="bg-accent-600 h-2 rounded-full transition-all duration-300"
                                                        style={{ width: `${Math.min((item.count / Math.max(...recent_activity.map(a => a.count))) * 100, 100)}%` }}
                                                    ></div>
                                                </div>
                                                <span className="font-bold text-accent-600 text-lg">{item.count}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        )}

                        {/* Content Manager - Content Overview */}
                        {hasPermission('blogs') && hasPermission('faqs') && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.8 }}
                                className="bg-gradient-to-br from-accent-50 to-gold-50 rounded-2xl p-6 shadow-lg border border-accent-100"
                            >
                                <div className="flex items-center mb-6">
                                    <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center mr-3">
                                        <PencilSquareIcon className="w-5 h-5 text-accent-600" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900">Content Overview</h3>
                                </div>
                                <div className="space-y-4">
                                    <div className="bg-white rounded-lg p-4 flex items-center justify-between">
                                        <div className="flex items-center">
                                            <div className="w-8 h-8 bg-accent-100 rounded-lg flex items-center justify-center mr-3">
                                                <PencilSquareIcon className="w-4 h-4 text-accent-600" />
                                            </div>
                                            <span className="text-sm font-medium text-gray-700">Total Blogs</span>
                                        </div>
                                        <span className="font-bold text-accent-600 text-xl">{overview.total_blogs}</span>
                                    </div>
                                    <div className="bg-white rounded-lg p-4 flex items-center justify-between">
                                        <div className="flex items-center">
                                            <div className="w-8 h-8 bg-secondary-100 rounded-lg flex items-center justify-center mr-3">
                                                <QuestionMarkCircleIcon className="w-4 h-4 text-secondary-600" />
                                            </div>
                                            <span className="text-sm font-medium text-gray-700">Total FAQs</span>
                                        </div>
                                        <span className="font-bold text-secondary-600 text-xl">{overview.total_faqs}</span>
                                    </div>
                                    <div className="bg-white rounded-lg p-4 flex items-center justify-between">
                                        <div className="flex items-center">
                                            <div className="w-8 h-8 bg-gold-100 rounded-lg flex items-center justify-center mr-3">
                                                <EyeIcon className="w-4 h-4 text-gold-600" />
                                            </div>
                                            <span className="text-sm font-medium text-gray-700">Content Status</span>
                                        </div>
                                        <span className="font-bold text-gold-600 text-lg">Active</span>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}

export default AdminDashboard
